import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { variacao } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { porcentagem, reais, type MesDaVisao, type SerieDaVisao } from "@/lib/dinheiro";

export type Cartao = { rotulo: string; valor: string; detalhe?: string; delta?: number | null; bomSeSobe?: boolean; cor?: string };

export function Indicador({ c }: { c: Cartao }) {
  const subiu = (c.delta ?? 0) > 0;
  const bom = c.bomSeSobe === undefined ? null : subiu === c.bomSeSobe;
  return (
    <div className="min-w-0 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm sm:p-4 dark:border-border dark:bg-card">
      <p className="truncate text-[11px] font-medium text-zinc-500 sm:text-xs dark:text-muted-foreground">{c.rotulo}</p>
      <p className={cn("mt-1 truncate text-base font-bold tabular-nums sm:text-xl", c.cor ?? "text-zinc-900 dark:text-foreground")}>{c.valor}</p>
      <div className="mt-0.5 flex min-h-[16px] items-center gap-1.5 text-[11px]">
        {c.delta != null && c.delta !== 0 && (
          <span className={cn("inline-flex items-center font-semibold", bom === null ? "text-zinc-500" : bom ? "text-emerald-600" : "text-red-600")}>
            {subiu ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {porcentagem(Math.abs(c.delta))}
          </span>
        )}
        {c.detalhe && <span className="truncate text-zinc-500 dark:text-muted-foreground">{c.detalhe}</span>}
      </div>
    </div>
  );
}

/** Os oito números do mês, com a variação contra o mês anterior. */
export function Indicadores({ serie, mes }: { serie: SerieDaVisao; mes: MesDaVisao | undefined }) {
  const atual = serie[serie.length - 1]!;
  const anterior = serie[serie.length - 2];
  const d = (k: "receitaCents" | "recebidoCents" | "custoVariavelCents" | "custosFixosCents" | "resultadoCents") => (anterior ? variacao(atual[k], anterior[k]) : null);
  const resultadoCor = atual.resultadoCents >= 0 ? "text-emerald-600" : "text-red-600";
  const equilibrio =
    atual.pontoDeEquilibrioCents == null
      ? { valor: "—", detalhe: "Sem margem para calcular" }
      : atual.faltaParaEquilibrioCents
        ? { valor: reais(atual.pontoDeEquilibrioCents), detalhe: `Faltam ${reais(atual.faltaParaEquilibrioCents)}` }
        : { valor: reais(atual.pontoDeEquilibrioCents), detalhe: "Atingido" };

  const cartoes: Cartao[] = [
    { rotulo: "Faturamento", valor: reais(atual.receitaCents), delta: d("receitaCents"), bomSeSobe: true, detalhe: "vs mês anterior" },
    { rotulo: "Recebido no mês", valor: reais(atual.recebidoCents), delta: d("recebidoCents"), bomSeSobe: true, cor: "text-emerald-600" },
    { rotulo: "A receber", valor: mes ? reais(mes.aReceberCents) : "…", detalhe: "Extratos em aberto" },
    { rotulo: "Custo variável", valor: reais(atual.custoVariavelCents), delta: d("custoVariavelCents"), bomSeSobe: false, detalhe: "Material e despesas" },
    { rotulo: "Margem de contribuição", valor: porcentagem(atual.margemPct), detalhe: reais(atual.margemCents) },
    { rotulo: "Custos fixos", valor: reais(atual.custosFixosCents), delta: d("custosFixosCents"), bomSeSobe: false },
    { rotulo: "Resultado", valor: reais(atual.resultadoCents), delta: d("resultadoCents"), bomSeSobe: true, cor: resultadoCor },
    { rotulo: "Ponto de equilíbrio", ...equilibrio, cor: atual.faltaParaEquilibrioCents ? "text-amber-600" : undefined },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
      {cartoes.map((c) => (
        <Indicador key={c.rotulo} c={c} />
      ))}
    </div>
  );
}
